import { useMemo, useRef, useState } from 'react';
import { Ban, GitPullRequestArrow, Link2, ListChecks, Paperclip, Search, X, CircleAlert } from '@/components/ui/icons';
import type { Task, TaskLink } from '@shared/types';
import { useDb } from '@/lib/store';
import { statusOf } from '@/lib/hierarchy';
import { addChecklist, addTaskLink, removeTaskLink, uploadAttachment } from '@/lib/actions';
import { useOpenTask } from '@/lib/nav';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { StatusIcon } from '@/components/fields/Status';
import { toast } from '@/components/ui/overlays';
import { uploadError } from '@/lib/upload';
import { IMAGES_ONLY, UPLOAD_ACCEPT, UPLOAD_LIMIT_TEXT } from '@shared/uploads';

function TaskSearch({ exclude, onPick }: { exclude: string; onPick: (id: string) => void }) {
  const tasks = useDb((db) => db.tasks);
  const lists = useDb((db) => db.lists);
  const [q, setQ] = useState('');
  const results = useMemo(() => {
    const ql = q.toLowerCase();
    return Object.values(tasks)
      .filter((t) => t.id !== exclude && !t.archived && (!ql || t.name.toLowerCase().includes(ql) || t.id === ql))
      .sort((a, b) => (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt))
      .slice(0, 12);
  }, [tasks, q, exclude]);
  return (
    <div className="menu" style={{ width: 360 }}>
      <div className="menu-search">
        <Search size={14} className="faint" />
        <input autoFocus placeholder="Search tasks..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {results.map((t) => (
        <button key={t.id} className="menu-item" onClick={() => onPick(t.id)}>
          <span className="menu-item-label truncate">{t.name}</span>
          <span className="menu-item-hint truncate">{lists[t.listId]?.name}</span>
        </button>
      ))}
      {results.length === 0 && <div className="menu-empty">No tasks found</div>}
    </div>
  );
}

function LinkRow({ link, other, label, tone }: { link: TaskLink; other: Task; label: string; tone?: 'warn' | 'block' }) {
  const openTask = useOpenTask();
  const status = useDb((db) => statusOf(db, other));
  return (
    <div className="tm-link-row" onClick={() => openTask(other.id)}>
      <span className={`tm-link-kind ${tone ?? ''}`}>{label}</span>
      <StatusIcon type={status.type} color={status.color} />
      <span className="truncate grow">{other.name}</span>
      <button
        className="icon-btn xs"
        onClick={(e) => {
          e.stopPropagation();
          removeTaskLink(link.id);
        }}
      >
        <X size={13} />
      </button>
    </div>
  );
}

export function RelationshipsSection({ task }: { task: Task }) {
  const links = useDb((db) => db.taskLinks);
  const tasks = useDb((db) => db.tasks);
  const fileRef = useRef<HTMLInputElement>(null);
  const mine = Object.values(links).filter((l) => l.fromTaskId === task.id || l.toTaskId === task.id);
  const waitingOn = mine.filter((l) => l.type === 'blocking' && l.toTaskId === task.id);
  const blocking = mine.filter((l) => l.type === 'blocking' && l.fromTaskId === task.id);
  const linked = mine.filter((l) => l.type === 'linked');
  const relMenu = (close: () => void) => (
    <Menu style={{ width: 230 }}>
      <MenuItem
        icon={<Link2 size={15} />}
        label="Link task"
        submenu={(c2) => <TaskSearch exclude={task.id} onPick={(id) => (addTaskLink(task.id, id, 'linked'), c2(), close())} />}
      />
      <MenuItem
        icon={<CircleAlert size={15} />}
        label="Waiting on"
        submenu={(c2) => <TaskSearch exclude={task.id} onPick={(id) => (addTaskLink(id, task.id, 'blocking'), c2(), close())} />}
      />
      <MenuItem
        icon={<Ban size={15} />}
        label="Blocking"
        submenu={(c2) => <TaskSearch exclude={task.id} onPick={(id) => (addTaskLink(task.id, id, 'blocking'), c2(), close())} />}
      />
    </Menu>
  );
  return (
    <>
      {mine.length > 0 && (
        <section className="tm-section">
          <div className="tm-section-head">
            <span className="tm-section-title" style={{ marginLeft: 4 }}>
              Relationships
            </span>
            <span className="spacer" />
            <Popover content={relMenu}>
              <button className="btn sm">Add</button>
            </Popover>
          </div>
          <div className="tm-links">
            {waitingOn.map((l) => tasks[l.fromTaskId] && <LinkRow key={l.id} link={l} other={tasks[l.fromTaskId]} label="Waiting on" tone="warn" />)}
            {blocking.map((l) => tasks[l.toTaskId] && <LinkRow key={l.id} link={l} other={tasks[l.toTaskId]} label="Blocking" tone="block" />)}
            {linked.map((l) => {
              const other = tasks[l.fromTaskId === task.id ? l.toTaskId : l.fromTaskId];
              return other ? <LinkRow key={l.id} link={l} other={other} label="Linked" /> : null;
            })}
          </div>
        </section>
      )}
      <div className="tm-actions">
        <Popover content={relMenu}>
          <button className="tm-action">
            <GitPullRequestArrow size={15} /> Relate items or add dependencies
          </button>
        </Popover>
        <button className="tm-action" onClick={() => addChecklist(task.id)}>
          <ListChecks size={15} /> Create checklist
        </button>
        <button className="tm-action" onClick={() => fileRef.current?.click()} title={UPLOAD_LIMIT_TEXT}>
          <Paperclip size={15} /> {IMAGES_ONLY ? 'Attach image' : 'Attach file'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={UPLOAD_ACCEPT}
          multiple
          hidden
          onChange={async (e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = '';
            for (const f of files) {
              try {
                await uploadAttachment(task.id, f);
              } catch (e) {
                toast(uploadError(e, f));
              }
            }
          }}
        />
      </div>
    </>
  );
}
